// videoState.ts — the pure, dependency-free lifecycle roll-up for the growth review console.
//
// Kept in its own module (no imports) so it is unit-testable with Node's built-in test runner
// without pulling the browser/Vite deps that api/growth.ts imports (client.ts, the auth store).
// api/growth.ts re-exports these for the page.
//
// Design (DESIGN-growth-review-console.md §STATE): video state is a roll-up of segment states,
// computed on read — there is NO stored lifecycle column.
//   pre        : no run yet (never sent to render)
//   production : has a run, never finished (first assembly)
//   post       : finished (assetUrl set), no segment reverted
//   post_pre   : finished, but ≥1 segment sent back for rework (a beat with reset steps)
// `assetUrl` is the durable "was ever post" anchor — it is set once by the render-complete webhook
// and PERSISTS through a per-segment revert (revert resets run_steps in the workers DB; assetUrl
// lives in the backend DB, untouched). `steps` tells which segments are currently reverted.

export type VideoState = "pre" | "production" | "post" | "post_pre";

/** The subset of a run step the roll-up needs (mirrors ops.ts RunStep.status/beatId). */
export interface RollupStep {
  beatId: string | null;
  status: string;
}

/** Statuses that mean a beat's work is reset / in-flight (i.e. the segment is "reverted" to pre). */
const REVERTED_STATUSES = new Set(["blocked", "ready", "running", "dispatched"]);

/**
 * Compute a video's lifecycle state. Pure; safe to call with or without step data.
 *
 * Without `steps` (list view), it reports the coarse state and never post_pre — correct, because
 * post_pre requires knowing a specific beat was reverted, which needs step data. With `steps`
 * (drill-in), a finished video with any reverted beat becomes post_pre.
 */
export function videoState(
  v: { runId: string | null; assetUrl: string | null },
  steps?: RollupStep[] | null,
): VideoState {
  if (!v.runId) return "pre";
  if (!v.assetUrl) return "production";
  // assetUrl set → the video was rendered (was post). Is a segment currently sent back?
  const reverted = (steps ?? []).some((s) => REVERTED_STATUSES.has(s.status));
  return reverted ? "post_pre" : "post";
}

/** Human label + colour for a video/segment state, for badges. */
export function videoStateBadge(state: VideoState): { label: string; color: string } {
  switch (state) {
    case "pre": return { label: "Pre-production", color: "#6b7280" };
    case "production": return { label: "In production", color: "#2563eb" };
    case "post": return { label: "Post-production", color: "#16a34a" };
    case "post_pre": return { label: "Revising (post→pre)", color: "#d97706" };
  }
}
